import React, { useEffect, useState, useMemo } from "react";
import { adminApi } from "../../lib/api";
import { PageHeader, SectionHeader, Kpi, Card, AwaitingBanner } from "./adminUi";
import {
  ExternalLink,
  RefreshCw,
  FlaskConical,
  Truck,
  CheckCircle2,
  XCircle,
  Image as ImageIcon,
  FileDown,
  CircleDollarSign,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "America/Chicago",
    });
  } catch {
    return iso;
  }
}
function fmtMoney(cents, currency) {
  if (cents == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${cents / 100} ${currency || "USD"}`;
  }
}

function StatusBadge({ value, kind }) {
  if (!value) return <span className="text-[#607467]">—</span>;
  const paid = kind === "payment" && value.toLowerCase() === "paid";
  const shipped = kind === "fulfillment" && ["shipped", "delivered"].includes(value.toLowerCase());
  const cls = paid || shipped
    ? "bg-[#1B2820] text-[#B7D2AF] border-[#4F6F52]/40"
    : value.toLowerCase().includes("refund") || value.toLowerCase().includes("cancel")
    ? "bg-[#241416] text-[#E7A6A6] border-[#8B4A50]/40"
    : "bg-[#1E2119] text-[#D0D2A6] border-[#7B7A45]/30";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider ${cls}`}
    >
      {value}
    </span>
  );
}

export default function AdminProverbCardOrdersPage() {
  const [rows, setRows] = useState(null);
  const [totals, setTotals] = useState(null);
  const [config, setConfig] = useState(null);
  const [err, setErr] = useState(null);
  const [includeUnpaid, setIncludeUnpaid] = useState(false);
  const [includeTest, setIncludeTest] = useState(false);
  const [limit, setLimit] = useState(50);
  const [reloading, setReloading] = useState(false);

  const load = async () => {
    setReloading(true);
    setErr(null);
    try {
      const r = await adminApi.proverbCardOrders({
        include_unpaid: includeUnpaid ? 1 : undefined,
        include_test: includeTest ? 1 : undefined,
        limit,
      });
      setRows(r.orders || []);
      setTotals(r.totals || null);
      setConfig(r.config || null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Detail may be a JSON object bubbled up from Proverb.
      setErr(typeof d === "string" ? d : d ? JSON.stringify(d, null, 2) : "Unable to load card orders");
      setRows([]);
      setTotals(null);
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [includeUnpaid, includeTest, limit]);

  const realSales = totals?.real_sales ?? 0;
  const testCount = totals?.test_orders ?? 0;
  const revenue = useMemo(
    () => fmtMoney(totals?.real_revenue_cents ?? 0, totals?.currency),
    [totals]
  );

  return (
    <div data-testid="admin-proverb-card-orders-page" className="space-y-8">
      <PageHeader
        eyebrow="I Need a Proverb"
        title="Card Orders"
        subtitle="Live read-only view from ineedaproverb.com. Test orders are labeled and never counted as real sales."
        right={
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-[#B7D2AF] inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                data-testid="cards-include-unpaid"
                checked={includeUnpaid}
                onChange={(e) => setIncludeUnpaid(e.target.checked)}
              />
              Include unpaid
            </label>
            <label className="text-xs text-[#B7D2AF] inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                data-testid="cards-include-test"
                checked={includeTest}
                onChange={(e) => setIncludeTest(e.target.checked)}
              />
              Show test orders
            </label>
            <select
              data-testid="cards-limit"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value, 10))}
              className="bg-[#141B18] border border-white/10 rounded-full text-xs px-2.5 py-1 text-white"
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} rows
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid="cards-refresh"
              onClick={load}
              className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-[#B7D2AF]"
            >
              <RefreshCw size={13} className={reloading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        }
      />

      <section>
        <SectionHeader label="Totals (real sales only)" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi testid="cards-total-real-sales" label="Real sales shown" value={realSales} tone="accent" />
          <Kpi testid="cards-total-revenue" label="Real revenue" value={revenue} tone="accent" />
          <Kpi
            testid="cards-total-test-orders"
            label="Test orders (excluded from revenue)"
            value={testCount}
            tone={testCount > 0 ? "warn" : "default"}
          />
          <Kpi
            testid="cards-total-excluded-unpaid"
            label="Excluded as unpaid"
            value={totals?.excluded_unpaid ?? 0}
          />
        </div>
        {config?.endpoint_host && (
          <div className="text-[11px] text-[#607467] mt-2">
            Source: <span className="font-mono">{config.endpoint_host}</span>
          </div>
        )}
      </section>

      {err && (
        <Card>
          <div className="flex items-start gap-2 text-sm text-[#E7A6A6]">
            <XCircle size={16} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold mb-1">Could not load card orders</div>
              <pre
                data-testid="cards-error-detail"
                className="text-xs whitespace-pre-wrap break-all text-[#C99A9A] bg-[#20100F] rounded-lg p-2 max-h-64 overflow-auto"
              >
                {err}
              </pre>
            </div>
          </div>
        </Card>
      )}

      {rows && rows.length === 0 && !err && (
        <AwaitingBanner label="No matching card orders." />
      )}

      {rows && rows.length > 0 && (
        <section>
          <SectionHeader label={`Orders (${rows.length}) — newest first`} />
          <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0F1512]">
            <table
              data-testid="cards-table"
              className="min-w-full text-sm border-separate border-spacing-0"
            >
              <thead className="text-[11px] uppercase tracking-wider text-[#8FA894]">
                <tr>
                  <Th>Order</Th>
                  <Th>Date</Th>
                  <Th>Buyer</Th>
                  <Th>Sponsor</Th>
                  <Th>Card / Qty</Th>
                  <Th>Paid</Th>
                  <Th>Payment</Th>
                  <Th>Fulfillment</Th>
                  <Th>Tracking</Th>
                  <Th>Assets</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr
                    key={o.id || o.order_number}
                    data-testid={`cards-row-${o.order_number || o.id}`}
                    className={`border-t border-white/5 ${
                      o.test_mode ? "bg-[#1B120E]/60" : ""
                    }`}
                  >
                    <Td>
                      <div className="flex items-center gap-1.5 font-mono text-xs text-white">
                        {o.order_number || o.id}
                        {o.test_mode && (
                          <span
                            data-testid={`cards-test-badge-${o.order_number || o.id}`}
                            className="inline-flex items-center gap-0.5 rounded-full border border-[#B4795C]/50 bg-[#241914] px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[#F3D6A6]"
                          >
                            <FlaskConical size={9} /> Test
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap text-[#B7D2AF] text-xs">
                      {fmtDate(o.order_date)}
                    </Td>
                    <Td>
                      <div className="text-white">{o.buyer_name || "—"}</div>
                      <div className="text-[11px] text-[#8FA894] font-mono">{o.buyer_email || "—"}</div>
                    </Td>
                    <Td className="text-[#B7D2AF] text-xs">{o.sponsor || "—"}</Td>
                    <Td>
                      <div className="text-white">{o.card_type || "—"}</div>
                      <div className="text-[11px] text-[#8FA894]">Qty: {o.quantity ?? "—"}</div>
                    </Td>
                    <Td className="whitespace-nowrap">
                      <div className="inline-flex items-center gap-1 text-white">
                        <CircleDollarSign size={13} className="text-[#B7D2AF]" />
                        {fmtMoney(o.total_paid_cents, o.currency)}
                      </div>
                    </Td>
                    <Td>
                      <StatusBadge value={o.payment_status} kind="payment" />
                    </Td>
                    <Td>
                      <StatusBadge value={o.fulfillment_status} kind="fulfillment" />
                    </Td>
                    <Td>
                      {o.tracking_code ? (
                        o.tracking_url ? (
                          <a
                            href={o.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#B7D2AF] hover:text-white text-xs"
                          >
                            <Truck size={12} /> {o.tracking_code}
                            <ExternalLink size={11} />
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#B7D2AF] text-xs">
                            <Truck size={12} /> {o.tracking_code}
                          </span>
                        )
                      ) : (
                        <span className="text-[#607467]">—</span>
                      )}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        {o.preview_front_url && (
                          <AssetLink href={o.preview_front_url} label="Front preview" icon={<ImageIcon size={12} />} />
                        )}
                        {o.preview_back_url && (
                          <AssetLink href={o.preview_back_url} label="Back preview" icon={<ImageIcon size={12} />} />
                        )}
                        {o.print_file_url && (
                          <AssetLink href={o.print_file_url} label="Print file" icon={<FileDown size={12} />} />
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-[#607467] mt-2 flex items-center gap-1">
            <CheckCircle2 size={11} className="text-[#4F6F52]" />
            {totals?.real_sales ?? 0} of {rows.length} rows counted toward real revenue.
            {testCount > 0 && (
              <span className="text-[#B4795C]">
                {" "}
                · {testCount} test order{testCount === 1 ? "" : "s"} shown for reference and never counted.
              </span>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left font-semibold px-3 py-2 border-b border-white/10 bg-[#141B18]/70 first:rounded-tl-2xl last:rounded-tr-2xl">
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

function AssetLink({ href, label, icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      className="inline-flex items-center gap-1 text-[11px] text-[#B7D2AF] hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-2 py-0.5"
    >
      {icon} {label}
    </a>
  );
}
